import React, {useCallback, useMemo} from 'react';
import classnames from 'classnames';
import {List} from 'immutable';
import {callTargetedAction, setUpStore, useFluxStore} from 'client/framework';
import MarkdownRendererV2 from 'generic/MarkdownRendererV2/MarkdownRendererV2.react';
import {Fieldset} from '@albert-io/atomic';
import type {QuestionModelV3} from '@albert-io/models';
import {useShallow} from 'zustand/react/shallow';
import {
  useQuestionTranslationStore,
  NoTranslationWarningHeader,
  btnDisabledWarning,
  translatedInstructions
} from 'components/QuestionTypes/shared/TranslationSection';
import {ALTERNATE_CONTENT_LANGUAGES} from 'components/QuestionTypes/shared/TranslationSection/Translation.types';
import AnnotatableContent from 'client/components/PracticeView/Annotations/AnnotatableContent/AnnotatableContent';
import {buildQuestionRegionId} from 'client/components/PracticeView/Annotations/shared/annotationRegions';
import {useIsAnnotationModeActive} from 'client/components/PracticeView/Annotations/mode/useIsAnnotationModeActive';
import {lockedGroupProps} from 'client/components/PracticeView/Annotations/shared/annotationFreeze';

import snippetSelectionQuestionActions from './SnippetSelectionQuestion.actions';
import SnippetSelectionQuestionStore from 'client/components/QuestionTypes/SnippetSelection/SnippetSelectionQuestion.store';

import './snippet-selection-question.scss';

interface SnippetSelectionInputAreaProps {
  questionId: string;
  question: QuestionModelV3;
  storeName: string;
  isGuessSubmitted: boolean;
}

/*
old propTypes
  static propTypes = {
    questionId: PropTypes.string,
    question: PropTypes.instanceOf(getModelForResourceType('question_v3')),
    storeName: PropTypes.string.isRequired,
    isGuessSubmitted: PropTypes.bool
  };
*/

const SnippetSelectionInputArea = ({
  questionId,
  question,
  storeName,
  isGuessSubmitted
}: SnippetSelectionInputAreaProps) => {
  const areAnswersLocked = useIsAnnotationModeActive() && !isGuessSubmitted;
  // Translation info
  const {translatedQuestionInfo, showTranslations} = useQuestionTranslationStore(
    useShallow((state) => ({
      translatedQuestionInfo: state.translatedQuestionInfo,
      showTranslations: state.showTranslations && state.translationSettings.question
    }))
  );

  // Single source for both the rendered text and the anchor lang, so they can't diverge.
  const translated = showTranslations ? translatedQuestionInfo : null;

  const {prompt, options} = useMemo(() => {
    if (translated) {
      return {
        prompt: translated.snippet_prompt,
        options: formatOptions(translated.options)
      };
    }
    return {
      prompt: question.getSnippetPrompt(),
      options: question.getOptions()
    };
  }, [translated, question]);

  // end translation info

  const store = useMemo(() => {
    // `setUpStore` is typed as returning the base `Store`. `hasResponse` is a
    // dynamic data field that may be present on the underlying snippet-selection
    // store instance, so surface it as an optional property for narrowing below.
    return setUpStore(SnippetSelectionQuestionStore, storeName, questionId) as ReturnType<
      typeof setUpStore
    > & {hasResponse?: boolean};
  }, [storeName, questionId]);
  // Bridge: without this subscription, surfaces outside the legacy re-render
  // cascade (e.g. diagnostics) never repaint snippet highlights after TOGGLE_OPTION.
  useFluxStore(store, (s) => s.readData());
  const hasResponse = Boolean(store?.hasResponse);

  const isInteractive = !(isGuessSubmitted || hasResponse || showTranslations);

  const onOptionClick = useCallback(
    (optionId: string) => {
      if (!isInteractive) return;
      callTargetedAction({
        name: snippetSelectionQuestionActions.TOGGLE_OPTION,
        targetStore: store?.getName(),
        payload: optionId
      });
    },
    [isInteractive, store]
  );

  const inputAreaClassNames = useMemo(
    () =>
      classnames('snippet-selection-question__input-area', {
        'snippet-selection-question__input-area--interactive': isInteractive
      }),
    [isInteractive]
  );

  return (
    <>
      {showTranslations && (
        <NoTranslationWarningHeader className='mb-x4' text={btnDisabledWarning} />
      )}
      <Fieldset
        {...lockedGroupProps(areAnswersLocked)}
        legend={showTranslations ? translatedInstructions.selectMultiple : 'Select answer(s) below'}
      >
        <div className='snippet-selection-question mb-x2'>
          <AnnotatableContent
            regionId={questionId ? buildQuestionRegionId(questionId, 'snippet') : null}
            lang={translated ? ALTERNATE_CONTENT_LANGUAGES[0] : null}
            className='contents'
          >
            <MarkdownRendererV2
              className={inputAreaClassNames}
              isGuessSubmitted={isGuessSubmitted}
              text={prompt}
              snippetSelectionData={{
                isExplanation: isGuessSubmitted,
                options,
                storeName: store?.getName(),
                onOptionClick,
                areAnswersLocked
              }}
              question={question}
              isBeingTranslated={showTranslations}
            />
          </AnnotatableContent>
        </div>
      </Fieldset>
    </>
  );
};

export {SnippetSelectionInputArea};

const formatOptions = (options: any | null) => {
  const formattedOptions =
    options === null
      ? []
      : options.map((option) => {
          return {
            ...option,
            getId: () => option.id
          };
        });

  return List(formattedOptions);
};
